import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Wifi, TrendingDown, BarChart3, Radio, FileText, ArrowRight, TrendingUp, Minus, ShieldAlert, User } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { CoverageMap } from "@/components/CoverageMap";
import { PlanComparator } from "@/components/PlanComparator";
import { QUALITY_INDICATORS, SPEED_FACTORS, TRAFFIC_PRACTICES, IMAGES } from "@/data/content";

const Section = ({ id, icon: Icon, eyebrow, title, children }) => (
  <section id={id} className="scroll-mt-24 py-16 border-b border-border last:border-0">
    <div className="flex items-center gap-3 mb-2">
      <div className="h-10 w-10 rounded-xl bg-accent grid place-items-center"><Icon className="h-5 w-5 text-primary" /></div>
      <span className="text-xs font-bold uppercase tracking-[0.2em] text-primary">{eyebrow}</span>
    </div>
    <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mb-8">{title}</h2>
    {children}
  </section>
);

const nav = [
  { id: "cobertura", label: "Mapas de cobertura", icon: MapPin },
  { id: "planes", label: "Comparador de planes", icon: Wifi },
  { id: "factores", label: "Factores de velocidad", icon: TrendingDown },
  { id: "indicadores", label: "Indicadores de calidad", icon: BarChart3 },
  { id: "trafico", label: "Gestión de tráfico", icon: Radio },
  { id: "pqr", label: "Procedimiento PQR", icon: FileText },
];

export default function Informacion() {
  const location = useLocation();
  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 100);
    }
  }, [location]);

  return (
    <div data-testid="informacion-page">
      <div className="bg-secondary text-secondary-foreground py-16 grain relative overflow-hidden">
        <div className="absolute -top-20 right-10 h-72 w-72 rounded-full bg-primary/20 blur-3xl" />
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-primary">Información importante para usuarios</span>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-3 max-w-3xl">Todo lo que necesitas saber sobre tu servicio</h1>
          <p className="mt-4 text-secondary-foreground/70 max-w-2xl text-lg">Cobertura, planes, calidad, prácticas de tráfico y tus derechos como usuario, en un solo lugar.</p>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* quick nav */}
        <div className="flex gap-2 overflow-x-auto py-6 -mx-4 px-4 sticky top-[72px] bg-background/80 backdrop-blur-xl z-30 border-b border-border">
          {nav.map((n) => (
            <a key={n.id} href={`#${n.id}`} data-testid={`info-nav-${n.id}`} className="whitespace-nowrap rounded-full border border-border px-4 py-2 text-sm font-medium hover:border-primary hover:text-primary transition-colors flex items-center gap-2">
              <n.icon className="h-4 w-4" /> {n.label}
            </a>
          ))}
        </div>

        <Section id="cobertura" icon={MapPin} eyebrow="Cobertura" title="Mapa de cobertura interactivo">
          <p className="text-muted-foreground mb-8 max-w-2xl">Explora las zonas donde Colnexx presta sus servicios. Pasa el cursor por los puntos del mapa o busca tu ciudad y barrio.</p>
          <CoverageMap />
        </Section>

        <Section id="planes" icon={Wifi} eyebrow="Planes y tarifas" title="Comparador de planes y tarifas">
          <p className="text-muted-foreground mb-10 max-w-2xl">Compara velocidades de descarga y carga, características y precios de cada plan.</p>
          <PlanComparator />
        </Section>

        <Section id="factores" icon={TrendingDown} eyebrow="Velocidad" title="Factores que limitan la velocidad de Internet">
          <p className="text-muted-foreground mb-8 max-w-2xl">La velocidad de tu conexión puede verse afectada por distintos factores. Diferenciamos los que están bajo control del operador y los externos.</p>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="rounded-3xl border border-border bg-card p-8">
              <h3 className="font-display font-bold text-xl mb-1">Bajo control del operador</h3>
              <p className="text-xs text-muted-foreground mb-5 uppercase tracking-wider">Colnexx trabaja para minimizarlos</p>
              <ul className="space-y-3">
                {SPEED_FACTORS.operador.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm"><span className="h-1.5 w-1.5 rounded-full bg-primary mt-2 shrink-0" />{f}</li>
                ))}
              </ul>
            </div>
            <div className="rounded-3xl border border-border bg-muted/40 p-8">
              <h3 className="font-display font-bold text-xl mb-1">Externos al operador</h3>
              <p className="text-xs text-muted-foreground mb-5 uppercase tracking-wider">Dependen del entorno del usuario</p>
              <ul className="space-y-3">
                {SPEED_FACTORS.externos.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm"><span className="h-1.5 w-1.5 rounded-full bg-muted-foreground mt-2 shrink-0" />{f}</li>
                ))}
              </ul>
            </div>
          </div>
        </Section>

        <Section id="indicadores" icon={BarChart3} eyebrow="Calidad" title="Indicadores de calidad del servicio de Internet">
          <p className="text-muted-foreground mb-8 max-w-2xl">Publicamos periódicamente los indicadores de calidad de nuestra red (valores de referencia del último trimestre).</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {QUALITY_INDICATORS.map((q, i) => {
              const Trend = q.trend === "up" ? TrendingUp : q.trend === "down" ? TrendingDown : Minus;
              return (
                <motion.div key={q.metric} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }} className="rounded-2xl border border-border bg-card p-6" data-testid={`indicator-${i}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs uppercase tracking-wider text-muted-foreground">{q.metric}</span>
                    <Trend className="h-4 w-4 text-primary" />
                  </div>
                  <div className="font-display text-4xl font-black text-primary mb-2">{q.value}</div>
                  <p className="text-sm text-muted-foreground">{q.detail}</p>
                </motion.div>
              );
            })}
          </div>
        </Section>

        <Section id="trafico" icon={Radio} eyebrow="Neutralidad de red" title="Prácticas de gestión de tráfico de Internet">
          <p className="text-muted-foreground mb-8 max-w-2xl">Aplicamos la gestión de tráfico de forma razonable, transparente y no discriminatoria, respetando la neutralidad de la red.</p>
          <div className="grid sm:grid-cols-2 gap-5">
            {TRAFFIC_PRACTICES.map((t) => (
              <div key={t.title} className="rounded-2xl border border-border bg-card p-6 flex gap-4">
                <div className="h-10 w-10 rounded-xl bg-accent grid place-items-center shrink-0"><ShieldAlert className="h-5 w-5 text-primary" /></div>
                <div>
                  <h3 className="font-display font-bold text-lg mb-1">{t.title}</h3>
                  <p className="text-sm text-muted-foreground">{t.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </Section>

        <Section id="pqr" icon={FileText} eyebrow="Tus derechos" title="Procedimiento y trámite de PQR">
          <div className="grid lg:grid-cols-2 gap-8 items-start">
            <div className="space-y-6">
              <div>
                <h3 className="font-display font-bold text-lg mb-2 flex items-center gap-2"><User className="h-5 w-5 text-primary" /> Derechos del usuario</h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>• Presentar peticiones, quejas y recursos (PQR) sin costo y por cualquier canal habilitado.</li>
                  <li>• Recibir respuesta dentro de los 15 días hábiles siguientes a la radicación.</li>
                  <li>• Beneficiarse del Silencio Administrativo Positivo si no hay respuesta oportuna.</li>
                  <li>• Interponer recurso de reposición y, en subsidio, apelación ante la respuesta.</li>
                  <li>• No pagar las sumas objeto de reclamación mientras se resuelve la PQR de facturación.</li>
                </ul>
              </div>
              <div>
                <h3 className="font-display font-bold text-lg mb-2">Canales disponibles</h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>• Formulario web de radicación de PQR (recomendado).</li>
                  <li>• WhatsApp y línea de atención telefónica.</li>
                  <li>• Correo electrónico y oficinas de atención.</li>
                </ul>
              </div>
              <div className="rounded-2xl bg-accent/60 border border-primary/20 p-5">
                <h4 className="font-display font-bold mb-2">PQR asociadas a facturación</h4>
                <p className="text-sm text-muted-foreground">Puedes reclamar valores facturados dentro de los plazos legales. Mientras se resuelve tu reclamo, no estás obligado a pagar las sumas objeto de reclamación; solo debes pagar los valores no reclamados. La respuesta se da dentro de los 15 días hábiles y podrás interponer recursos de reposición y, en subsidio, apelación.</p>
              </div>
            </div>
            <div className="rounded-3xl overflow-hidden border border-border">
              <img src={IMAGES.support} alt="Atención al usuario Colnexx" className="w-full h-56 object-cover" />
              <div className="p-8 bg-card">
                <h3 className="font-display text-2xl font-bold mb-2">Radica tu PQR en línea</h3>
                <p className="text-muted-foreground mb-6">Diligencia el formulario y recibe de inmediato tu constancia de radicación en PDF con el número de radicado y el plazo de respuesta.</p>
                <Link to="/pqr"><Button size="lg" className="rounded-full font-semibold gap-2 w-full" data-testid="info-pqr-cta">Ir al formulario de PQR <ArrowRight className="h-4 w-4" /></Button></Link>
              </div>
            </div>
          </div>
        </Section>
      </div>
    </div>
  );
}
